import React, { useRef, useEffect, useCallback } from 'react';
import type { HSV, HarmonyMode } from './types';
import { hsvToRgb } from '../../utils/colors';

interface WheelCanvasProps {
  size: number;
  brightness: number;
  handles: HSV[];
  activeIndex: number;
  canDragAll: boolean;
  mode: HarmonyMode;
  complementIndex: number; // -1 if no complement label
  endIndices: number[];    // indices labeled "E"
  onHandleDrag: (index: number, hsv: HSV) => void;
  onDragEnd: () => void;
  onActiveChange: (index: number) => void;
  onDragStart?: () => void;
  onDragMove?: (canvasX: number, canvasY: number) => void;
  onBitmapReady?: (canvas: HTMLCanvasElement) => void;
}

const OUTER_RATIO = 0.95;
const BASE_HANDLE_R = 14;
const DEP_HANDLE_R = 10;

const hsvToXY = (hsv: HSV, cx: number, cy: number, radius: number): { x: number; y: number } => {
  const angle = ((hsv.h - 90) * Math.PI) / 180;
  const outer = radius * OUTER_RATIO;
  const dist = (hsv.s / 100) * outer;
  return {
    x: cx + Math.cos(angle) * dist,
    y: cy + Math.sin(angle) * dist,
  };
};

const xyToHS = (x: number, y: number, cx: number, cy: number, radius: number): { h: number; s: number } => {
  const dx = x - cx;
  const dy = y - cy;
  let angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
  if (angle < 0) angle += 360;
  const h = angle % 360;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const outer = radius * OUTER_RATIO;
  const s = Math.max(0, Math.min(100, Math.round((dist / outer) * 100)));
  return { h: Math.round(h), s };
};

const renderWheelBitmap = (canvas: HTMLCanvasElement, size: number, brightness: number) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  canvas.width = size;
  canvas.height = size;

  const cx = size / 2;
  const cy = size / 2;
  const outerR = (size / 2) * OUTER_RATIO;
  const v = Math.max(5, Math.min(100, brightness));

  const imageData = ctx.createImageData(size, size);
  const data = imageData.data;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= outerR + 1.5) {
        let angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
        if (angle < 0) angle += 360;
        const hue = angle % 360;
        const sat = Math.min((dist / outerR) * 100, 100);

        const rgb = hsvToRgb(hue, sat, v);
        const alpha = dist <= outerR
          ? 255
          : Math.max(0, Math.round(((outerR + 1.5 - dist) / 1.5) * 255));
        const idx = (y * size + x) * 4;
        data[idx] = rgb.r;
        data[idx + 1] = rgb.g;
        data[idx + 2] = rgb.b;
        data[idx + 3] = alpha;
      }
    }
  }

  ctx.putImageData(imageData, 0, 0);
};

/** Get the handle label for a given index. */
const getHandleLabel = (
  index: number,
  complementIndex: number,
  endIndices: number[],
): string | null => {
  if (index === 0) return 'B';
  if (index === complementIndex) return 'C';
  if (endIndices.includes(index)) return 'E';
  return null;
};

/** Modes that draw polygon edges instead of radial lines from base. */
const POLYGON_MODES: ReadonlySet<string> = new Set(['triad', 'square']);

/** Modes that draw a single B→E line. */
const LINE_MODES: ReadonlySet<string> = new Set(['monochromatic']);

/** Core vertex counts for polygon modes. */
const POLYGON_CORE: Partial<Record<string, number>> = { triad: 3, square: 4 };

const WheelCanvas: React.FC<WheelCanvasProps> = ({
  size,
  brightness,
  handles,
  activeIndex,
  canDragAll,
  mode,
  complementIndex,
  endIndices,
  onHandleDrag,
  onDragEnd,
  onActiveChange,
  onDragStart,
  onDragMove,
  onBitmapReady,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const offscreenRef = useRef<HTMLCanvasElement | null>(null);
  const draggingRef = useRef<number | null>(null);
  const lastKeyRef = useRef('');
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

  const ensureBitmap = useCallback(() => {
    const key = `${size}_${brightness}`;
    if (lastKeyRef.current === key && offscreenRef.current) return;
    const offscreen = document.createElement('canvas');
    renderWheelBitmap(offscreen, size, brightness);
    offscreenRef.current = offscreen;
    lastKeyRef.current = key;
    onBitmapReady?.(offscreen);
  }, [size, brightness, onBitmapReady]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ensureBitmap();

    const w = size * dpr;
    const h = size * dpr;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);

    if (offscreenRef.current) {
      ctx.drawImage(offscreenRef.current, 0, 0, size, size);
    }

    const cx = size / 2;
    const cy = size / 2;
    const radius = size / 2;
    const positions = handles.map((hv) => hsvToXY(hv, cx, cy, radius));

    // Shades mode: only draw B handle, no lines
    const isShades = mode === 'shades';

    // ── Connection Lines ──
    if (handles.length > 1 && !isShades) {
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.6)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);

      if (POLYGON_MODES.has(mode)) {
        // Draw polygon edges connecting core vertices
        const coreCount = POLYGON_CORE[mode] ?? 3;
        const core = positions.slice(0, Math.min(coreCount, positions.length));
        if (core.length >= 2) {
          ctx.beginPath();
          ctx.moveTo(core[0].x, core[0].y);
          for (let i = 1; i < core.length; i++) {
            ctx.lineTo(core[i].x, core[i].y);
          }
          ctx.closePath();
          ctx.stroke();
        }
        // Draw spokes from center to each core vertex
        for (let i = 0; i < Math.min(coreCount, positions.length); i++) {
          ctx.beginPath();
          ctx.moveTo(cx, cy);
          ctx.lineTo(core[i].x, core[i].y);
          ctx.stroke();
        }
      } else if (LINE_MODES.has(mode)) {
        // Mono: draw B→E line
        const basePos = positions[0];
        const endPos = positions[positions.length - 1];
        ctx.beginPath();
        ctx.moveTo(basePos.x, basePos.y);
        ctx.lineTo(endPos.x, endPos.y);
        ctx.stroke();
      } else {
        // Default: radial lines from base to each dependent
        const basePos = positions[0];
        for (let i = 1; i < positions.length; i++) {
          ctx.beginPath();
          ctx.moveTo(basePos.x, basePos.y);
          ctx.lineTo(positions[i].x, positions[i].y);
          ctx.stroke();
        }
      }

      ctx.restore();
    }

    // ── Handles ──
    const visibleHandles = isShades ? [{ hsv: handles[0], idx: 0 }] : handles.map((hsv, idx) => ({ hsv, idx }));
    visibleHandles.forEach(({ hsv, idx: i }) => {
      const pos = positions[i];
      const label = getHandleLabel(i, complementIndex, endIndices);
      const isLabeled = label !== null;
      const isActive = i === activeIndex;
      const r = (i === 0 || isLabeled) ? BASE_HANDLE_R : DEP_HANDLE_R;

      const rgb = hsvToRgb(hsv.h, hsv.s, hsv.v);
      const fillColor = `rgb(${rgb.r},${rgb.g},${rgb.b})`;

      // Shadow
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, r + 2, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.15)';
      ctx.fill();

      // White border
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();

      // Color fill
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, r - 3, 0, Math.PI * 2);
      ctx.fillStyle = fillColor;
      ctx.fill();

      // Active indicator
      if (isActive) {
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r + 4, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(99,102,241,0.7)';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Label (B, C, E)
      if (label) {
        // Choose text color based on fill brightness
        const lum = 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
        ctx.fillStyle = lum > 128 ? '#1e293b' : '#ffffff';
        ctx.font = 'bold 9px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, pos.x, pos.y);
      }
    });
  }, [size, handles, activeIndex, ensureBitmap, dpr, mode, complementIndex, endIndices]);

  useEffect(() => {
    draw();
  }, [draw]);

  const hitTest = (clientX: number, clientY: number): number => {
    const canvas = canvasRef.current;
    if (!canvas) return -1;
    const rect = canvas.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * size;
    const y = ((clientY - rect.top) / rect.height) * size;
    const cx = size / 2;
    const cy = size / 2;
    const radius = size / 2;

    // In shades mode, only B handle is visible on wheel
    const testIndices = mode === 'shades' ? [0] : Array.from({ length: handles.length }, (_, i) => i);
    for (let j = testIndices.length - 1; j >= 0; j--) {
      const i = testIndices[j];
      const pos = hsvToXY(handles[i], cx, cy, radius);
      const dx = x - pos.x;
      const dy = y - pos.y;
      const hitR = i === 0 ? BASE_HANDLE_R + 6 : DEP_HANDLE_R + 6;
      if (dx * dx + dy * dy <= hitR * hitR) return i;
    }
    return -1;
  };

  const getHSFromPointer = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * size;
    const y = ((clientY - rect.top) / rect.height) * size;
    return xyToHS(x, y, size / 2, size / 2, size / 2);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    const hitIdx = hitTest(e.clientX, e.clientY);

    if (hitIdx >= 0) {
      const canDrag = hitIdx === 0 || canDragAll;
      if (canDrag) {
        draggingRef.current = hitIdx;
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        onDragStart?.();
        const coords = getCanvasCoords(e.clientX, e.clientY);
        if (coords) onDragMove?.(coords.x, coords.y);
      }
      onActiveChange(hitIdx);
    } else {
      const hs = getHSFromPointer(e.clientX, e.clientY);
      if (hs) {
        draggingRef.current = 0;
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        onDragStart?.();
        onHandleDrag(0, { h: hs.h, s: hs.s, v: handles[0].v });
        onActiveChange(0);
        const coords = getCanvasCoords(e.clientX, e.clientY);
        if (coords) onDragMove?.(coords.x, coords.y);
      }
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (draggingRef.current === null) return;
    const hs = getHSFromPointer(e.clientX, e.clientY);
    if (!hs) return;

    const idx = draggingRef.current;
    onHandleDrag(idx, { h: hs.h, s: hs.s, v: handles[idx].v });

    const coords = getCanvasCoords(e.clientX, e.clientY);
    if (coords) onDragMove?.(coords.x, coords.y);
  };

  const handlePointerUp = () => {
    if (draggingRef.current !== null) {
      draggingRef.current = null;
      onDragEnd();
    }
  };

  const getCanvasCoords = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * size,
      y: ((clientY - rect.top) / rect.height) * size,
    };
  };

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-auto cursor-crosshair touch-none select-none"
      style={{ width: size, height: size, maxWidth: '100%', aspectRatio: '1' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    />
  );
};

export default WheelCanvas;
